import { MultiPolygon } from 'geojson';

import { Abstract, Coordinates, VerticalBounds } from '../commons/commons.types';
import { Drone } from './drone.types';
import { FlySection } from './route.types';

export type FlightPlanCriteria = {
  sites: string[];
  droneCodes?: string[];
  fromDate?: string | null;
  toDate?: string | null;
  alsoArchived?: boolean;
};

export enum FlightPlanStateEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum ActivationStatusEnum {
  ACCEPTED = 'ACCEPTED',
  SUBMITTED = 'SUBMITTED',
  REJECTED = 'REJECTED',
  DRAFT = 'DRAFT',
}

export enum FlightPlanActivationStateEnum {
  DONE = 'DONE',
  ACTIVE = 'ACTIVE',
  UPCOMING = 'UPCOMING',
}

export enum FlightPlanTypeEnum {
  CLASSIC = 'CLASSIC',
  PRECONFIGURED = 'PRECONFIGURED',
}

export interface FlightPlanPortion {
  section: FlySection;
}

export type Interval = {
  from: string;
  to: string;
};

export type FlightPlanConflict = {
  conflictingFlightPlanCode: string;
  conflictingActivationId: number;
  interval: Interval;
};

export type Activation = {
  id: number;
  startDate: string;
  endDate: string;
  status: ActivationStatusEnum;
  conflicts: FlightPlanConflict[];
  validatedOrRejectedBy: string | null;
  validationOrRejectionTime: string | null;
  state: FlightPlanActivationStateEnum;
};

export enum LinkedDroneDetectionEnum {
  NO_DRONE_DETECTION = 'NO_DRONE_DETECTION',
  DRONE_DETECTION_INSIDE = 'DRONE_DETECTION_INSIDE',
  DRONE_DETECTION_OUTSIDE = 'DRONE_DETECTION_OUTSIDE',
}

export enum Provider {
  HOLOGARDE = 'HOLOGARDE',
  CLEARANCE = 'CLEARANCE',
  VOLOCOPTERE = 'VOLOCOPTERE',
  UNKNOWN = 'UNKNOWN',
}

export enum FlightPlanVersion {
  ARCHIVED = 'ARCHIVED',
  CURRENT = 'CURRENT',
}

export type AbstractFlightPlan = {
  id: number;
  isProtocol: boolean;
  site: string;
  code: string;
  name: string;
  drones: Drone[];
  startTime: string;
  endTime: string;
  startPosition: Coordinates | null;
  polygons: MultiPolygon;
  provider: Provider;
  organization: string | null;
  externalId: string | null;
  activations: Activation[];
  state: FlightPlanStateEnum;
  activationId: number | null;
  linkedDroneDetection: LinkedDroneDetectionEnum | null;
} & Abstract &
  VerticalBounds;

export type ClassicFlightPlan = {
  type: FlightPlanTypeEnum.CLASSIC;
} & AbstractFlightPlan;

export type PreconfiguredFlightPlan = {
  type: FlightPlanTypeEnum.PRECONFIGURED;
  version: FlightPlanVersion;
  flyZoneStart: string;
  flyZoneEnd: string;
  portions: FlightPlanPortion[];
  origin: PreconfiguredFlightPlan | null;
} & AbstractFlightPlan;

export type ActivationAndFlightPlan = {
  activation: Activation;
  flightPlan: AbstractFlightPlan;
};

export enum ActivationSortTypeEnum {
  FP_NAME = 'FP_NAME',
  START_DATE = 'START_DATE',
  MAX_HEIGHT = 'MAX_HEIGHT',
  PROVIDER = 'PROVIDER',
  STATUS = 'STATUS',
  CONFLICT = 'CONFLICT',
}
export type SortTypeAndDirection = {
  sortTYpe: ActivationSortTypeEnum;
  ascending: boolean;
};

export type ActivationCriteria = {
  organizations?: string[];
  externalIds?: string[];
  collaborativeIds?: string[];
  from?: string;
  to?: string;
  statuses?: ActivationStatusEnum[];
  withConflicts?: boolean | null;
  sortTypeAndDirection?: SortTypeAndDirection;
};
